import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';
import edgeMftService from '../services/edgeMftService';
import { useAuth } from '../contexts/AuthContext';

const steps = ['Generate Secret', 'Verify Setup', 'Save Backup Codes'];

const OTPSetup: React.FC = () => {
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [otpEnabled, setOtpEnabled] = useState(false);

  // Step 1: Generate secret
  const [secret, setSecret] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');

  // Step 2: Verify setup
  const [verificationCode, setVerificationCode] = useState('');

  // Step 3: Backup codes
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copiedCodes, setCopiedCodes] = useState(false);

  // Retry tracking
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 5;

  // Check OTP status on mount
  useEffect(() => {
    checkOTPStatus();
  }, []);

  useEffect(() => {
    if (activeStep === 0 && !secret && !otpEnabled && user?.username) {
      handleGenerateSecret();
    }
  }, [activeStep, otpEnabled, user?.username]);

  useEffect(() => {
    if (qrCodeUrl) {
      QRCode.toDataURL(qrCodeUrl, { width: 300 })
        .then(url => setQrCodeDataUrl(url))
        .catch(err => console.error('QR Code generation error:', err));
    }
  }, [qrCodeUrl]);

  const checkOTPStatus = async () => {
    try {
      const userInfo = await edgeMftService.getUserInfo();
      setOtpEnabled(userInfo.otp_enabled);
    } catch (err) {
      console.error('Failed to get OTP status', err);
      setOtpEnabled(false);
    }
  };

  const handleGenerateSecret = async () => {
    console.log('handleGenerateSecret called', { user, username: user?.username, retryCount });

    // If user is not available yet, wait a bit and retry (with limit)
    if (!user?.username) {
      if (retryCount < maxRetries) {
        console.warn('User not available yet, retrying...', retryCount + 1, '/', maxRetries);
        setRetryCount(retryCount + 1);
        setTimeout(handleGenerateSecret, 500);
        return;
      } else {
        console.error('Max retries reached, user still not available');
        setError('User not authenticated. Please refresh the page and try again.');
        return;
      }
    }

    setLoading(true);
    setError('');
    setRetryCount(0); // Reset retry count on success

    try {
      console.log('Calling setupOTP with username:', user.username);
      const result = await edgeMftService.setupOTP(user.username);
      setSecret(result.secret);
      setQrCodeUrl(result.qr_code_url);
      setBackupCodes(result.backup_codes);
    } catch (err: any) {
      console.error('OTP setup error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to generate OTP secret');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const username = user?.username || '';
    console.log('handleVerifySetup called', { user, username, hasSecret: !!secret, code: verificationCode });

    if (!username) {
      setError('User not authenticated. Please refresh the page and try again.');
      setLoading(false);
      return;
    }

    try {
      console.log('Calling verifyOTPSetup with:', { username, secret: secret.substring(0, 8) + '...' });
      await edgeMftService.verifyOTPSetup(username, secret, verificationCode);
      setSuccess('OTP code verified successfully!');

      // Move to next step after a short delay
      setTimeout(() => {
        setActiveStep(2);
        setSuccess('');
      }, 1500);
    } catch (err: any) {
      console.error('OTP verification error:', err);
      setError(err.response?.data?.error || err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableOTP = async () => {
    setLoading(true);
    setError('');

    const username = user?.username || '';
    console.log('handleEnableOTP called', { user, username, hasSecret: !!secret, backupCodesCount: backupCodes.length });

    if (!username) {
      setError('User not authenticated. Please refresh the page and try again.');
      setLoading(false);
      return;
    }

    try {
      console.log('Calling enableOTP with username:', username);
      await edgeMftService.enableOTP(username, secret, backupCodes);
      setSuccess('Two-factor authentication has been enabled successfully!');
      setOtpEnabled(true);

      // Could redirect or show a completion message
      setTimeout(() => {
        window.location.reload(); // Reload to update auth state
      }, 2000);
    } catch (err: any) {
      console.error('OTP enable error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to enable OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableOTP = async () => {
    if (!window.confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      return;
    }

    setLoading(true);
    setError('');

    const username = user?.username || '';
    if (!username) {
      setError('User not authenticated. Please refresh the page and try again.');
      setLoading(false);
      return;
    }

    try {
      await edgeMftService.disableOTP(username);
      setSuccess('Two-factor authentication has been disabled.');
      setOtpEnabled(false);

      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err: any) {
      console.error('OTP disable error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to disable OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    setSuccess('Secret key copied to clipboard!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleCopyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    setCopiedCodes(true);
    setSuccess('Backup codes copied to clipboard!');
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleNext = () => {
    if (activeStep === 1) {
      // Verify before moving to backup codes
      return;
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>
            Two-Factor Authentication
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 4 }}>
            {otpEnabled
              ? 'Two-factor authentication is currently enabled for your account.'
              : 'Add an extra layer of security to your account by enabling two-factor authentication (2FA).'
            }
          </Typography>

          {/* If OTP is already enabled, show status and disable option */}
          {otpEnabled ? (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}

              <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 3 }}>
                Two-factor authentication is active and protecting your account.
              </Alert>

              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                Your account is protected with two-factor authentication. You'll need to enter a code from your authenticator app each time you log in.
              </Typography>

              <Button
                variant="outlined"
                color="error"
                onClick={handleDisableOTP}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Disable Two-Factor Authentication'}
              </Button>
            </>
          ) : (
            <>
              {/* Original setup flow */}

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Step 1: Generate and display QR code */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Step 1: Scan QR Code
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.)
              </Typography>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {qrCodeDataUrl && (
                    <Box sx={{ textAlign: 'center', my: 3 }}>
                      <img src={qrCodeDataUrl} alt="QR Code" />
                    </Box>
                  )}

                  <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Or enter this key manually:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                        {secret}
                      </Typography>
                      <Tooltip title="Copy secret key">
                        <IconButton size="small" onClick={handleCopySecret}>
                          <CopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button variant="contained" onClick={handleNext}>
                      Next
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* Step 2: Verify the setup */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Step 2: Verify Setup
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Enter the 6-digit code from your authenticator app to verify the setup.
              </Typography>

              <Box component="form" onSubmit={handleVerifySetup} sx={{ mt: 3 }}>
                <TextField
                  required
                  fullWidth
                  label="Verification Code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={loading}
                  placeholder="000000"
                  inputProps={{
                    maxLength: 6,
                    pattern: '[0-9]*',
                    inputMode: 'numeric',
                  }}
                  helperText="Enter the 6-digit code from your authenticator app"
                  autoFocus
                  sx={{ mb: 3 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button onClick={handleBack} disabled={loading}>
                    Back
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || verificationCode.length !== 6}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Verify'}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* Step 3: Display backup codes */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Step 3: Save Your Backup Codes
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Store these backup codes in a safe place. Each code can only be used once if you lose access to your authenticator app.
              </Alert>

              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2">
                    Backup Codes
                  </Typography>
                  <Button
                    startIcon={copiedCodes ? <CheckIcon /> : <CopyIcon />}
                    size="small"
                    onClick={handleCopyBackupCodes}
                    color={copiedCodes ? 'success' : 'primary'}
                  >
                    {copiedCodes ? 'Copied' : 'Copy All'}
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  {backupCodes.map((code, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={code}
                        primaryTypographyProps={{
                          fontFamily: 'monospace',
                          fontSize: '0.95rem',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>

              <Alert severity="info" sx={{ mb: 3 }}>
                Make sure you've saved these codes before proceeding. You won't be able to see them again.
              </Alert>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={handleBack} disabled={loading}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleEnableOTP}
                  disabled={loading || !copiedCodes}
                >
                  {loading ? <CircularProgress size={24} /> : 'Enable 2FA'}
                </Button>
              </Box>
            </Box>
          )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default OTPSetup;
